import React, { useMemo } from 'react';
import { StepContainer, NavigationButtons, InfoBox } from '../index';
import { ActivityBoundary } from '../../components/ActivityBoundary';
import { useWizard } from '../../wizard';

interface GuardianCardProps {
  label: string;
  name?: string;
  email?: string;
  signed?: boolean;
  onSign: () => void;
}

const GuardianCard: React.FC<GuardianCardProps> = ({ label, name, email, signed, onSign }) => {
  const statusText = signed ? 'Underskrevet' : 'Afventer underskrift';

  return (
    <div className="info-box" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div>
        <strong>{label}</strong>
        <div>{name || 'Navn mangler'}</div>
        {email && <div style={{ opacity: 0.85 }}>{email}</div>}
      </div>
      <div
        data-testid={`${label}-status`}
        style={{
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--border-radius, 8px)',
          background: signed ? 'rgba(34, 197, 94, 0.12)' : 'rgba(234, 179, 8, 0.15)',
          color: signed ? '#166534' : '#92400e'
        }}
      >
        {statusText}
      </div>
      <button className="btn btn-secondary" onClick={onSign} disabled={!!signed} type="button">
        {signed ? 'Underskrift simuleret' : 'Simulér underskrift'}
      </button>
    </div>
  );
};

const DriverParentSignatureStep: React.FC = () => {
  const { selfServiceData, updateSelfServiceData, goNext, goBack } = useWizard();

  const requiresParentSignature = selfServiceData.dlParentConsentRequested === true;
  const custodyType = selfServiceData.dlCustodyType ?? 'ene';
  const notifySecondGuardian = selfServiceData.dlNotifySecondGuardian === true;
  const showSecondGuardian = requiresParentSignature && custodyType === 'med' && notifySecondGuardian;

  const primaryName = selfServiceData.dlGuardian1Name?.trim() || selfServiceData.user?.name || 'Den forælder/værge der er logget ind';
  const primaryEmail = selfServiceData.dlGuardian1Email?.trim() || selfServiceData.user?.email;

  const guardian1Signed = !!selfServiceData.dlGuardian1Signed;
  const guardian2Signed = !!selfServiceData.dlGuardian2Signed;

  const allSigned = useMemo(() => {
    if (!requiresParentSignature) {return true;}
    if (custodyType === 'med') {
      if (notifySecondGuardian) {
        return guardian1Signed && guardian2Signed;
      }
      return guardian1Signed;
    }
    return guardian1Signed;
  }, [requiresParentSignature, custodyType, guardian1Signed, guardian2Signed, notifySecondGuardian]);

  const handleSignGuardian1 = () => {
    updateSelfServiceData('dlGuardian1Signed', true);
  };

  const handleSignGuardian2 = () => {
    updateSelfServiceData('dlGuardian2Signed', true);
  };

  const handleNext = () => {
    if (!allSigned) {return;}
    goNext();
  };

  const infoCopy = requiresParentSignature
    ? [
        'Vi afsender en digital samtykkeerklæring (P23T) til de forældre/værger du har angivet.',
        'Når hver forælder har underskrevet med MitID, fortsætter ansøgningen automatisk.'
      ]
    : ['Der kræves ikke forældresamtykke for denne ansøgning.'];

  const digitalPostNote = showSecondGuardian && notifySecondGuardian
    ? 'Vi sender også besked via Digital Post til forælder 2.'
    : null;

  return (
    <StepContainer title="Forældrenes underskrift">
      <InfoBox type="info">
        {infoCopy.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
        {digitalPostNote && <p>{digitalPostNote}</p>}
      </InfoBox>

      {requiresParentSignature ? (
        <ActivityBoundary mode="visible">
        <div
          style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            marginBottom: '1.5rem'
          }}
        >
          <GuardianCard
            label="Registreret underskriver"
            name={primaryName}
            email={primaryEmail}
            signed={guardian1Signed}
            onSign={handleSignGuardian1}
          />
          <ActivityBoundary mode={showSecondGuardian ? 'visible' : 'hidden'}>
            <GuardianCard
              label="Forælder/værge 2"
              name={selfServiceData.dlGuardian2Name}
              email={selfServiceData.dlGuardian2Email}
              signed={guardian2Signed}
              onSign={handleSignGuardian2}
            />
          </ActivityBoundary>
        </div>
      </ActivityBoundary>) : (
        <p>Du er over 18 år. Derfor kræves der ikke samtykke fra forældre til denne ansøgning.</p>
      )}

      <NavigationButtons onBack={goBack} onNext={handleNext} nextDisabled={!allSigned} />
    </StepContainer>
  );
};

export default DriverParentSignatureStep;
